import { Link } from 'react-router-dom';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { Dashboard } from 'shared';
import BarList from '@/components/charts/BarList';
import type { ParticipantStats } from '@/hooks/useReports';
import { formatCount, formatHours } from '@/lib/reportFormat';

interface PendingProps {
  pending: Dashboard['pending'];
  /** Only the super admin can activate a staff account. */
  canManageStaff: boolean;
}

/** What waits for someone to act. It belongs to no centre, so no filter hides it. */
export function PendingCard({ pending, canManageStaff }: PendingProps) {
  const nothing = pending.consents === 0 && pending.staffAccounts === 0;

  return (
    <section className="card p-5" aria-labelledby="pending">
      <h2 id="pending" className="text-sm font-semibold text-foreground">
        Por tratar
      </h2>
      <p className="text-xs text-muted-foreground mt-0.5">Em todos os centros</p>

      {nothing ? (
        <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
          <CheckCircle2 className="w-4 h-4 shrink-0" aria-hidden="true" />
          Nada à espera.
        </p>
      ) : (
        <ul className="mt-4 space-y-3">
          {pending.consents > 0 && (
            <li className="flex items-start gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-yellow-600 dark:text-yellow-400" aria-hidden="true" />
              <span className="text-foreground">
                <span className="font-semibold tabular-nums">{formatCount(pending.consents)}</span>{' '}
                {pending.consents === 1 ? 'menor sem' : 'menores sem'} consentimento do encarregado de educação.{' '}
                {/* Consent is recorded on the participant, in Utilizadores. */}
                <Link to="/users" className="font-medium text-primary hover:underline">
                  Ver utilizadores
                </Link>
              </span>
            </li>
          )}
          {pending.staffAccounts > 0 && (
            <li className="flex items-start gap-2 text-sm">
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0 text-yellow-600 dark:text-yellow-400" aria-hidden="true" />
              <span className="text-foreground">
                <span className="font-semibold tabular-nums">{formatCount(pending.staffAccounts)}</span>{' '}
                {pending.staffAccounts === 1 ? 'conta da equipa' : 'contas da equipa'} à espera de aprovação.{' '}
                {canManageStaff ? (
                  <Link to="/admin-users" className="font-medium text-primary hover:underline">
                    Aprovar
                  </Link>
                ) : (
                  <span className="text-muted-foreground">Só o super administrador pode aprovar.</span>
                )}
              </span>
            </li>
          )}
        </ul>
      )}
    </section>
  );
}

/** Everyone registered, and in what state. */
export function ParticipantsCard({ stats }: { stats: ParticipantStats | undefined }) {
  return (
    <section className="card p-5" aria-labelledby="participants">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="participants" className="text-sm font-semibold text-foreground">
          Participantes registados
        </h2>
        <Link to="/users" className="text-xs font-medium text-primary hover:underline">
          Ver todos
        </Link>
      </div>

      {!stats ? (
        <p className="mt-4 text-sm text-muted-foreground">Sem dados.</p>
      ) : (
        <>
          <p className="mt-3 text-3xl font-bold text-foreground tabular-nums">{formatCount(stats.total)}</p>
          <dl className="mt-3 grid grid-cols-3 gap-2 text-xs">
            <div>
              <dt className="text-muted-foreground">Ativos</dt>
              <dd className="text-sm font-semibold text-foreground tabular-nums">{formatCount(stats.active)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Pendentes</dt>
              <dd className="text-sm font-semibold text-foreground tabular-nums">{formatCount(stats.pending)}</dd>
            </div>
            <div>
              <dt className="text-muted-foreground">Inativos</dt>
              <dd className="text-sm font-semibold text-foreground tabular-nums">{formatCount(stats.inactive)}</dd>
            </div>
          </dl>
          <p className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">
            <span className="font-semibold text-foreground tabular-nums">{formatCount(stats.newThisMonth)}</span> novos
            este mês, {formatCount(stats.newLastMonth)} no mês passado.
          </p>
        </>
      )}
    </section>
  );
}

/** The equipment used the longest over the last thirty days. */
export function TopEquipmentCard({ equipment }: { equipment: Dashboard['topEquipment'] }) {
  return (
    <section className="card p-5" aria-labelledby="top-equipment">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="top-equipment" className="text-sm font-semibold text-foreground">
          Equipamento mais usado
        </h2>
        <Link to="/reports" className="text-xs font-medium text-primary hover:underline">
          Ver relatório
        </Link>
      </div>
      <p className="text-xs text-muted-foreground mt-0.5">Horas de utilização nos últimos 30 dias</p>

      <div className="mt-4">
        {equipment.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">Sem utilização registada.</p>
        ) : (
          <BarList
            formatValue={formatHours}
            color="var(--chart-3)"
            rows={equipment.map((e) => ({ key: e.name, label: e.name, value: e.hours }))}
          />
        )}
      </div>
    </section>
  );
}
